/**
 * Persistent Geometry Cache using IndexedDB
 * Caches geometry data across browser sessions for faster loading
 */

class GeometryCache {
  constructor(options = {}) {
    this.dbName = options.dbName || 'coandacloud-geometry-cache';
    this.storeName = 'geometry';
    this.version = 1;
    this.db = null;
    this.maxSizeBytes = options.maxSizeBytes || 500 * 1024 * 1024; // 500MB default
    this.maxAgeMs = options.maxAgeMs || 7 * 24 * 60 * 60 * 1000; // 7 days default
    this.ready = this.init();
  }

  /**
   * Initialize IndexedDB connection
   */
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        console.error('[GeometryCache] Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('[GeometryCache] IndexedDB initialized');
        // Clean up expired entries on startup
        this.cleanup().catch(console.warn);
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'key' });
          store.createIndex('casePath', 'casePath', { unique: false });
          store.createIndex('cachedAt', 'cachedAt', { unique: false });
          console.log('[GeometryCache] Created object store');
        }
      };
    });
  }

  /**
   * Generate a cache key from geometry parameters
   */
  generateKey(params) {
    const { casePath, time, colorField, selectedParts } = params;
    const partsKey = selectedParts ? JSON.stringify(selectedParts.sort()) : 'all';
    return `${casePath}|${time}|${colorField}|${partsKey}`;
  }

  /**
   * Estimate size of geometry data in bytes
   */
  estimateSize(data) {
    try {
      // Rough estimate: JSON stringify and count bytes
      const json = JSON.stringify(data);
      return new Blob([json]).size;
    } catch {
      return 0;
    }
  }

  /**
   * Get cached geometry data
   * @param {Object} params - Must include casePath, time, colorField, and cacheKey
   * @param {string} params.cacheKey - Server-provided key that changes when simulation data changes
   */
  async get(params) {
    await this.ready;

    const { cacheKey } = params;
    const key = this.generateKey(params);

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(key);

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          // Check if expired by age
          const age = Date.now() - result.cachedAt;
          if (age > this.maxAgeMs) {
            console.log('[GeometryCache] Entry expired by age:', key);
            this.delete(key).catch(console.warn);
            resolve(null);
            return;
          }

          // Check if simulation data has changed (cacheKey mismatch)
          if (cacheKey && result.cacheKey && result.cacheKey !== cacheKey) {
            console.log('[GeometryCache] Simulation changed, invalidating cache:', key);
            console.log('[GeometryCache] Old cacheKey:', result.cacheKey, 'New:', cacheKey);
            this.clearCase(params.casePath).catch(console.warn);
            resolve(null);
            return;
          }

          console.log('[GeometryCache] Cache HIT:', key);
          resolve(result.data);
        } else {
          console.log('[GeometryCache] Cache MISS:', key);
          resolve(null);
        }
      };

      request.onerror = () => {
        console.error('[GeometryCache] Get failed:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Store geometry data in cache
   * @param {Object} params - Must include casePath, time, colorField, and cacheKey
   * @param {string} params.cacheKey - Server-provided key for cache invalidation
   */
  async set(params, data) {
    await this.ready;

    const key = this.generateKey(params);
    const sizeBytes = this.estimateSize(data);

    // Ensure we have space
    await this.ensureSpace(sizeBytes);

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      const entry = {
        key,
        casePath: params.casePath,
        time: params.time,
        colorField: params.colorField,
        cacheKey: params.cacheKey,  // Store for later validation
        data,
        sizeBytes,
        cachedAt: Date.now()
      };

      const request = store.put(entry);

      request.onsuccess = () => {
        console.log('[GeometryCache] Cached:', key, `(${(sizeBytes / 1024 / 1024).toFixed(2)}MB)`);
        resolve();
      };

      request.onerror = () => {
        console.error('[GeometryCache] Set failed:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Delete a specific cache entry
   */
  async delete(key) {
    await this.ready;

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all cache entries for a specific case
   */
  async clearCase(casePath) {
    await this.ready;

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('casePath');
      const request = index.openCursor(IDBKeyRange.only(casePath));

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          console.log('[GeometryCache] Cleared cache for case:', casePath);
          resolve();
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all cache entries
   */
  async clearAll() {
    await this.ready;

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();

      request.onsuccess = () => {
        console.log('[GeometryCache] All cache cleared');
        resolve();
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get total cache size in bytes
   */
  async getTotalSize() {
    await this.ready;

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.openCursor();

      let totalSize = 0;

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          totalSize += cursor.value.sizeBytes || 0;
          cursor.continue();
        } else {
          resolve(totalSize);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Ensure there's enough space for new data (LRU eviction)
   */
  async ensureSpace(neededBytes) {
    await this.ready;

    const totalSize = await this.getTotalSize();

    if (totalSize + neededBytes <= this.maxSizeBytes) {
      return; // Enough space
    }

    console.log('[GeometryCache] Cache full, evicting old entries...');

    // Get all entries sorted by cachedAt (oldest first)
    const entries = await this.getAllEntries();
    entries.sort((a, b) => a.cachedAt - b.cachedAt);

    let freedSpace = 0;
    const targetFree = neededBytes + (this.maxSizeBytes * 0.1); // Free 10% extra

    for (const entry of entries) {
      if (totalSize - freedSpace + neededBytes <= this.maxSizeBytes) {
        break;
      }
      await this.delete(entry.key);
      freedSpace += entry.sizeBytes;
      console.log('[GeometryCache] Evicted:', entry.key);
    }
  }

  /**
   * Get all cache entries (for eviction/stats)
   */
  async getAllEntries() {
    await this.ready;

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clean up expired entries
   */
  async cleanup() {
    await this.ready;

    const now = Date.now();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.openCursor();

      let cleaned = 0;

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const age = now - cursor.value.cachedAt;
          if (age > this.maxAgeMs) {
            cursor.delete();
            cleaned++;
          }
          cursor.continue();
        } else {
          if (cleaned > 0) {
            console.log('[GeometryCache] Cleaned up', cleaned, 'expired entries');
          }
          resolve(cleaned);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get cache statistics
   */
  async getStats() {
    await this.ready;

    const entries = await this.getAllEntries();
    const totalSize = entries.reduce((sum, e) => sum + (e.sizeBytes || 0), 0);
    const cases = new Set(entries.map(e => e.casePath));

    return {
      entryCount: entries.length,
      totalSizeBytes: totalSize,
      totalSizeMB: (totalSize / 1024 / 1024).toFixed(2),
      caseCount: cases.size,
      maxSizeMB: (this.maxSizeBytes / 1024 / 1024).toFixed(0)
    };
  }
}

// Export as singleton
window.GeometryCache = GeometryCache;
window.geometryCache = new GeometryCache();
